import * as React from "react";

const MOBILE_BREAKPOINT = 768;

export function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState<boolean>(
    () => typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT,
  );

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const onChange = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return isMobile;
}

const DESKTOP_BREAKPOINT = 1024; // Tailwind `lg`

/** True at >= lg. Used to drive the SERP map+list hybrid (desktop) vs the
 *  list/map toggle (mobile/tablet). */
export function useIsDesktop() {
  const [isDesktop, setIsDesktop] = React.useState<boolean>(
    () => typeof window !== "undefined" && window.innerWidth >= DESKTOP_BREAKPOINT,
  );
  React.useEffect(() => {
    const mql = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`);
    const onChange = () => setIsDesktop(window.innerWidth >= DESKTOP_BREAKPOINT);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return isDesktop;
}

const XL_BREAKPOINT = 1280; // Tailwind `xl`

/** True at >= xl — czyli tam, gdzie górny pasek wyszukiwarki pokazuje pastylki ceny,
 *  pokoi i metrażu. Poniżej tej szerokości tych pastylek nie ma i te same filtry muszą
 *  być widoczne jako chipy. Osobny próg od `useIsDesktop` (lg), bo pastylki pojawiają
 *  się dopiero przy xl — pomylenie tych dwóch progów dałoby okno 1024–1279 px, w którym
 *  filtr nie byłby widoczny nigdzie. */
export function useIsXl() {
  const [isXl, setIsXl] = React.useState<boolean>(
    () => typeof window !== "undefined" && window.innerWidth >= XL_BREAKPOINT,
  );
  React.useEffect(() => {
    const mql = window.matchMedia(`(min-width: ${XL_BREAKPOINT}px)`);
    const onChange = () => setIsXl(window.innerWidth >= XL_BREAKPOINT);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return isXl;
}

/**
 * URZĄDZENIE DOTYKOWE — decyzja po SPOSOBIE OBSŁUGI, nie po szerokości (zgłoszenie Piotra
 * z iPada, 28.09). iPad poziomo ma 1194 px, czyli układ pulpitowy, ale klawiatura systemowa
 * zasłania mu ~2/3 ekranu: pole do pisania w pasku u góry strony zostawiało nad klawiaturą
 * kilka linijek podpowiedzi. Tam, gdzie GŁÓWNYM wskaźnikiem jest palec, pole obszaru otwiera
 * więc pełnoekranową nakładkę z telefonu (pole na górze, podpowiedzi pod nim).
 *
 * `(hover: none) and (pointer: coarse)` opisuje wskaźnik GŁÓWNY: iPad z gładzikiem albo
 * myszką go nie spełnia i dostaje zwykłe pole — wtedy ma też klawiaturę fizyczną, więc nic
 * nie zasłania podpowiedzi. Laptop z ekranem dotykowym: główny wskaźnik to gładzik, więc
 * również zostaje przy polu.
 */
const ZAPYTANIE_DOTYKU = "(hover: none) and (pointer: coarse)";

export function useDotyk() {
  const [dotyk, setDotyk] = React.useState<boolean>(
    () => typeof window !== "undefined" && !!window.matchMedia?.(ZAPYTANIE_DOTYKU).matches,
  );
  React.useEffect(() => {
    const mql = window.matchMedia?.(ZAPYTANIE_DOTYKU);
    if (!mql) return;
    const onChange = () => setDotyk(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return dotyk;
}
